import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import {
  ChevronRight,
  Layers,
  Eye,
  MousePointerClick,
  DollarSign,
  Users,
  BarChart2,
} from 'lucide-react';
import { FbCampaign, getInsights } from '@/services/facebook-campaigns.service';
import { FbAdSet } from '@/services/facebook-ad-sets.service';
import { FbAd } from '@/services/facebook-ads.service';
import { InsightsTrendPoint } from '@/services/facebook-insights.service';
import { CampaignSparkline } from '@/components/facebook/CampaignSparkline';
import {
  formatMetaCurrency,
  formatMetaCount,
  formatMetaInsightsValue,
  formatMetaPercent,
} from '@/utils/currency';
import {
  StatusPill,
  ObjectivePill,
  PlatformIcons,
  ActionsDropdown,
  InlineErrorAlert,
} from './SharedComponents';
import AdsetSection from './AdsetSection';
import { AdWithCreative } from './AdCard';

interface CampaignCardProps {
  campaign: FbCampaign;
  currency?: string;
  expanded: boolean;
  onToggleExpand: () => void;
  /**
   * Daily insights trend for this campaign — used to draw the inline
   * sparkline. Undefined = still loading; empty array = no data yet.
   */
  trend?: InsightsTrendPoint[];
  // Adsets / ads (lazy, optional)
  adSets?: FbAdSet[];
  adSetsLoading?: boolean;
  adsByAdSet?: Record<string, AdWithCreative[]>;
  adsLoading?: boolean;
  // Errors / per-row loading
  error?: string;
  isLoading?: boolean;
  adSetErrors?: Record<string, string>;
  adSetLoadingIds?: Set<string>;
  adErrors?: Record<string, string>;
  adLoadingIds?: Set<string>;
  // Action handlers
  onToggleStatus?: () => void;
  onSyncInsights?: () => void;
  onArchive?: () => void;
  onDelete?: () => void;
  onDismissError?: () => void;
  onAdSetToggleStatus?: (adset: FbAdSet) => void;
  onAdSetArchive?: (adset: FbAdSet) => void;
  onAdSetDismissError?: (adsetId: string) => void;
  onAdToggleStatus?: (ad: FbAd) => void;
  onAdArchive?: (ad: FbAd) => void;
  onAdDismissError?: (adId: string) => void;
}

export const CampaignCard: React.FC<CampaignCardProps> = ({
  campaign,
  currency = '',
  expanded,
  onToggleExpand,
  trend,
  adSets,
  adSetsLoading,
  adsByAdSet,
  adsLoading,
  error,
  isLoading,
  adSetErrors,
  adSetLoadingIds,
  adErrors,
  adLoadingIds,
  onToggleStatus,
  onSyncInsights,
  onArchive,
  onDelete,
  onDismissError,
  onAdSetToggleStatus,
  onAdSetArchive,
  onAdSetDismissError,
  onAdToggleStatus,
  onAdArchive,
  onAdDismissError,
}) => {
  const nav = useNavigate();
  const status = campaign.effective_status ?? campaign.status;
  const ins = getInsights(campaign);

  const handleHeaderClick = () => onToggleExpand();

  return (
    <div
      onClick={handleHeaderClick}
      className={clsx(
        'cursor-pointer rounded-2xl border border-l-4 border-border bg-surface shadow-soft transition-all duration-200',
        'hover:shadow-elevated',
        error ? 'border-l-danger' : 'border-l-primary',
      )}
    >
      <div className="px-5 py-4">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            {/* Derive platform icons from the server-computed `platforms`
                annotation. When ad-sets aren't synced yet (empty array)
                we fall back to showing both badges — the historical
                default for this view. */}
            {(() => {
              const p = campaign.platforms ?? [];
              const knownPlatforms = p.length > 0;
              const hasFb = knownPlatforms ? p.includes('facebook') : true;
              const hasIg = knownPlatforms ? p.includes('instagram') : true;
              return <PlatformIcons className="mt-0.5" hasFacebook={hasFb} hasInstagram={hasIg} />;
            })()}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/facebook/campaigns/${campaign.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="truncate text-[15px] font-semibold leading-tight text-text-primary hover:text-primary"
                  title={campaign.name}
                >
                  {campaign.name}
                </Link>
                <StatusPill status={status} size="small" />
                <ObjectivePill objective={campaign.objective} />
                {/* Cross-platform badge — a campaign whose ad sets target
                    BOTH Facebook AND Instagram shows in both modules, and
                    this pill warns the operator that edits apply to both
                    placements. */}
                {(campaign.platforms ?? []).includes('facebook') &&
                 (campaign.platforms ?? []).includes('instagram') && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border border-info/30 bg-info-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-info"
                    title="This campaign's ad sets target both Facebook and Instagram. Edits apply to both placements."
                  >
                    Cross-platform
                  </span>
                )}
              </div>
              <div className="mt-0.5 truncate font-mono text-[11px] text-text-muted">
                {campaign.id}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ActionsDropdown
              entityType="campaign"
              currentStatus={status}
              isLoading={isLoading}
              onView={() => nav(`/facebook/campaigns/${campaign.id}`)}
              onToggleStatus={onToggleStatus}
              onSyncInsights={onSyncInsights}
              onArchive={onArchive}
              onDelete={onDelete}
            />
          </div>
        </div>

        {error && <InlineErrorAlert message={error} onDismiss={onDismissError} />}

        {/* Stats row */}
        {ins && (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-lg bg-surface-alt px-3 py-2 text-[11px]">
            <Stat icon={<Eye size={11} />} label="Reach" value={formatMetaCount(ins.reach)} />
            <Divider />
            <Stat
              icon={<BarChart2 size={11} />}
              label="Impr"
              value={formatMetaCount(ins.impressions)}
            />
            <Divider />
            <Stat
              icon={<MousePointerClick size={11} />}
              label="Clicks"
              value={formatMetaCount(ins.clicks)}
            />
            <Divider />
            <Stat icon={<MousePointerClick size={11} />} label="CTR" value={formatMetaPercent(ins.ctr)} />
            <Divider />
            <Stat
              icon={<DollarSign size={11} />}
              label="Spend"
              value={formatMetaInsightsValue(ins.spend, currency)}
              accent
            />
            <Divider />
            <Stat
              icon={<DollarSign size={11} />}
              label="CPM"
              value={formatMetaInsightsValue(ins.cpm, currency)}
            />
            <Divider />
            <Stat
              icon={<Users size={11} />}
              label="Reach freq"
              value={String(ins.frequency ?? '—')}
            />
          </div>
        )}

        {/* Trend sparkline — only when there are 2+ data points so we don't
            render an empty row for new/just-created campaigns. */}
        {trend && trend.length >= 2 && (
          <div className="mt-2">
            <CampaignSparkline data={trend} metric="spend" />
          </div>
        )}

        {/* Budget row */}
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[11px] text-text-muted">
          {campaign.daily_budget ? (
            <span>
              Budget{' '}
              <span className="font-semibold text-text-primary">
                {formatMetaCurrency(campaign.daily_budget, currency)}
              </span>{' '}
              <span className="text-text-muted">/ day</span>
            </span>
          ) : campaign.lifetime_budget ? (
            <span>
              Lifetime{' '}
              <span className="font-semibold text-text-primary">
                {formatMetaCurrency(campaign.lifetime_budget, currency)}
              </span>
            </span>
          ) : (
            <span>
              Budget <span className="font-semibold text-warning">Ad Set Budget</span>
            </span>
          )}
          {campaign.budget_remaining && (
            <span>
              Remaining{' '}
              <span className="font-semibold text-text-primary">
                {formatMetaCurrency(campaign.budget_remaining, currency)}
              </span>
            </span>
          )}
          {campaign.created_time && (
            <span>
              Created{' '}
              <span className="font-semibold text-text-primary">
                {new Date(campaign.created_time).toLocaleDateString()}
              </span>
            </span>
          )}
        </div>

        {/* Expand toggle */}
        <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-hover"
          >
            <motion.span
              animate={{ rotate: expanded ? 90 : 0 }}
              transition={{ duration: 0.16 }}
              className="inline-flex"
            >
              <ChevronRight size={14} />
            </motion.span>
            <Layers size={12} />
            <span>
              {expanded
                ? 'Hide ad sets'
                : adSets
                  ? `Show ${adSets.length} ad set${adSets.length === 1 ? '' : 's'}`
                  : 'Show ad sets'}
            </span>
          </button>
          <Link
            to={`/facebook/campaigns/${campaign.id}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-xs font-semibold text-text-secondary hover:text-primary"
          >
            <Eye size={12} />
            View details
          </Link>
        </div>
      </div>

      {/* Adsets panel */}
      {expanded && (
        <div className="space-y-3 px-5 pb-5">
          {adSetsLoading && !adSets && (
            <>
              {[0, 1].map((i) => (
                <div key={i} className="skeleton h-32 rounded-xl" />
              ))}
            </>
          )}

          {adSets && adSets.length === 0 && (
            <div className="rounded-xl border border-dashed border-border bg-surface-alt px-4 py-6 text-center text-xs text-text-muted">
              No ad sets in this campaign yet.
            </div>
          )}

          {adSets?.map((adSet) => (
            <AdsetSection
              key={adSet.id}
              adSet={adSet}
              ads={adsByAdSet?.[adSet.id]}
              adsLoading={adsLoading}
              currency={currency}
              error={adSetErrors?.[adSet.id]}
              isLoading={adSetLoadingIds?.has(adSet.id)}
              adErrors={adErrors}
              adLoadingIds={adLoadingIds}
              onToggleStatus={() => onAdSetToggleStatus?.(adSet)}
              onArchive={() => onAdSetArchive?.(adSet)}
              onDismissError={() => onAdSetDismissError?.(adSet.id)}
              onAdToggleStatus={onAdToggleStatus}
              onAdArchive={onAdArchive}
              onAdDismissError={onAdDismissError}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  accent?: boolean;
}> = ({ icon, label, value, accent }) => (
  <span className="inline-flex items-center gap-1">
    <span className="text-text-muted">{icon}</span>
    <span className="text-text-muted">{label}</span>
    <span className={clsx('font-bold tabular-nums', accent ? 'text-primary' : 'text-text-primary')}>
      {value}
    </span>
  </span>
);

const Divider: React.FC = () => <span className="h-3 w-px bg-border-strong" />;

export default CampaignCard;
